import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import FolderList from '@/library/FolderList.vue'
import * as constants from '@/library/library_constants'
const ComponentLib = () => import('@/componentlibrary/ComponentLib.vue')
const AdminAnalytics = () => import('@/views/AdminAnalytics.vue')
const AdminUserList = () => import('@/admin/AdminUserList.vue')
const LibraryFolder = () => import('@/library/LibraryFolder.vue')
const ViewOnlyLibraryFolder = () => import('@/library/ViewOnlyLibraryFolder.vue')
const RecentlySaved = () => import('@/library/recently_saved/RecentlySaved.vue')
const RecentlyViewed = () => import('@/library/recently_viewed/RecentlyViewed.vue')
const PresentlevelsContainer = () => import('@/presentlevels/PresentlevelsContainer.vue')
const PresentlevelsSummaries = () => import('@/presentlevels/PresentlevelsSummaries.vue')
const PresentlevelsSessionWrapper = () => import('@/presentlevels/PresentlevelsSessionWrapper.vue')
const PresentlevelsNewSessionForm = () => import('@/presentlevels/PresentlevelsNewSessionForm.vue')
const PresentlevelsSavedSummaryWrapper = () => import('@/presentlevels/PresentlevelsSavedSummaryWrapper.vue')
const PresentlevelsSavedSummary = () => import('@/presentlevels/PresentlevelsSavedSummary.vue')
const ImpactOfDisability = () => import('@/impact-of-disability/ImpactOfDisability.vue')
const ImpactOfDisabilitySavedStatementWrapper = () => import('@/impact-of-disability/ImpactOfDisabilitySavedStatementWrapper.vue')
const ImpactOfDisabilitySavedStatement = () => import('@/impact-of-disability/ImpactOfDisabilitySavedStatement.vue')
const SharedImpactOfDisabilityStatement = () => import('@/impact-of-disability/SharedImpactOfDisabilityStatement.vue')
const {LIBRARY_MODE_ALL, LIBRARY_MODE_ARCHIVED, LIBRARY_MODE_OWNED, LIBRARY_MODE_SHARED} = constants

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_ROUTER_BASE),
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (browser back/forward), use it
    if (savedPosition) {
      return savedPosition
    }
    // Otherwise, scroll to top
    return { top: 0, left: 0 }
  },
  routes: [
    {
      path: '/toolkit-vue3-spa',
      name: 'home',
      component: HomeView,
      meta: {
        title: 'Engineer Landing',
      }
    },
    {
      path: '/components',
      name: 'ComponentLib',
      component: ComponentLib,
      meta: {
        title: 'Component Library',
        requiresAuth: true,
        adminAccess: true
      }
    },
    {
      path: '/admin/analytics',
      name: 'AdminAnalytics',
      component: AdminAnalytics,
      meta: {
        title: 'Analytics Dashboard',
        requiresAuth: true,
        requiresInternalOrSchoolAdmin: true
      }
    },
    {
      path: '/admin/user_list/:schoolUuid?',
      name: 'AdminUserList',
      component: AdminUserList,
      meta: {
        title: 'Admin User List',
        requiresAuth: true,
        requiresInternalOrSchoolAdmin: true,
        requiresReloginCheck: true,
      }
    },
    {
      path: '/share/folder/:shareToken',
      name: 'ViewOnlyLibraryFolder',
      component: ViewOnlyLibraryFolder,
      props: true,
      meta: {
        title: 'Shared Folder',
        requiresAuth: false
      }
    },
    {
      path: '/share/iod/:uuid',
      name: 'SharedImpactOfDisabilityStatement',
      component: SharedImpactOfDisabilityStatement,
      props: true,
      meta: {
        title: 'Impact of Disability Statement',
        requiresAuth: false
      }
    },
    {
      path: '/share/presentlevels/summary/:uuid',
      name: 'SharedPresentlevelsSummary',
      component: SharedPresentLevelsStatement,
      props: true,
      meta: {
        title: 'Present Levels Statement',
        requiresAuth: false
      }
    },
    {
      path: '/library/all',
      name: 'AllFolders',
      component: FolderList,
      props: {
        mode: LIBRARY_MODE_ALL
      },
      meta: {
        title: 'All Folders',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true
      }
    },
    {
      path: '/library',
      name: 'Library',
      redirect: '/library/all'
    },
    {
      path: '/library/folder/:uuid',
      redirect: (to) => { return { path: `/folder/${to.params.uuid}` } }
    },
    {
      path: '/folder/:uuid',
      name: 'LibraryFolder',
      component: LibraryFolder,
      props: true,
      meta: {
        title: 'Library Folder',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true
      }
    },
    {
      path: '/library/recently_saved',
      name: 'RecentlySaved',
      component: RecentlySaved,
      meta: {
        title: 'Recently Saved',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true,
        isPremium: true
      }
    },
    {
      path: '/library/allresources',
      redirect: '/library/recently_saved'
    },
    {
      path: '/library/archived',
      name: 'ArchivedFolders',
      component: FolderList,
      props: {
        mode: LIBRARY_MODE_ARCHIVED
      },
      meta: {
        title: 'Archived Folders',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true,
        isPremium: true
      }
    },
    {
      path: '/library/recently_viewed/:type',
      name: 'RecentlyViewed',
      props: true,
      component: RecentlyViewed,
      meta: {
        title: 'Recently Viewed',
        requiresAuth: true,
        noIndex: true
      }
    },
    {
      path: '/library/shared',
      name: 'SharedWithMe',
      component: FolderList,
      props: {
        mode: LIBRARY_MODE_SHARED
      },
      meta: {
        title: 'All Folders: Shared with Me',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true,
        isPremium: true
      }
    },
    {
      path: '/library/owned',
      name: 'MyFolders',
      component: FolderList,
      props: {
        mode: LIBRARY_MODE_OWNED
      },
      meta: {
        title: 'All Folders: My Folders',
        requiresAuth: true,
        requiresReloginCheck: true,
        noIndex: true,
        isPremium: true
      },
    },
    {
      path: '/share/student/:shareToken',
      redirect: (to) => ({ path: `/share/folder/${to.params.shareToken}` })
    },
    {
      path: '/student/:uuid',
      redirect: (to) => ({ path: `/folder/${to.params.uuid}` })
    },
    {
      path: '/presentlevels/v2',
      name: 'Presentlevels',
      component: PresentlevelsContainer,
      meta: {
        title: 'Present Levels Wizard',
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'PresentlevelsSessionWrapper',
          component: PresentlevelsSessionWrapper,
          meta: {
            title: 'Present Levels Wizard',
            requiresAuth: true,
          },
          children: [
            {
              path: 'session',
              name: 'PresentlevelsSession',
              meta: {
                title: 'Present Levels Wizard',
                requiresAuth: true,
              },
              children: [
                {
                  path: '',
                  name: 'PresentlevelsNewSession',
                  component: PresentlevelsNewSessionForm,
                  meta: {
                    title: 'Present Levels Wizard',
                    requiresAuth: true,
                  }
                },
                {
                  path: ':session_uuid',
                  name: 'PresentlevelsCurrentSession',
                  component: PresentlevelsSummaries,
                  props: true,
                  meta: {
                    title: 'Present Levels Wizard',
                    requiresAuth: true,
                  }
                }
              ]
            }
          ]
        },
        {
          path: 'summary',
          name: 'PresentlevelsSavedSummaryWrapper',
          component: PresentlevelsSavedSummaryWrapper,
          meta: {
            title: 'Present Levels Wizard',
            requiresAuth: true,
          },
          children: [
            {
              path: ':summary_uuid',
              name: 'PresentlevelsSavedSummary',
              component: PresentlevelsSavedSummary,
              props: true,
              meta: {
                title: 'Present Levels Wizard',
                requiresAuth: true,
              }
            }
          ]
        }
      ]
    },
    {
      path: '/impact-of-disability',
      name: 'ImpactOfDisability',
      component: ImpactOfDisability,
      meta: {
        title: 'Impact of Disability Statement',
        requiresAuth: true,
        requiresReloginCheck: true
      }
    },
    {
      path: '/impact-of-disability/v2',
      redirect: '/impact-of-disability',
    },
    {
      path: '/library/iod/:statement_uuid',
      name: 'ImpactOfDisabilitySavedStatementWrapper',
      component: ImpactOfDisabilitySavedStatementWrapper,
      meta: {
        title: 'Impact of Disability Statement',
        requiresAuth: true,
      },
      children: [
        {
          path: '',
          name: 'ImpactOfDisabilitySavedStatement',
          component: ImpactOfDisabilitySavedStatement,
          props: true,
          meta: {
            title: 'Impact of Disability Statement',
            requiresAuth: true,
          }
        }
      ]
    },
  ]
})

import { authRouteGuard, reloginRouteGuard, betaRouteGuard, premiumRouteGuard } from "@/router/auth_guards.ts";
import { internalOrSchoolAdminRouteGuard } from "@/router/internal-or-school-admin-route-guard.ts";
import { useRouterStore } from './router.store'
import SharedPresentLevelsStatement from '@/presentlevels/SharedPresentLevelsStatement.vue'


router.beforeEach(authRouteGuard)
router.beforeEach(reloginRouteGuard)
router.beforeEach(betaRouteGuard)
router.beforeEach(internalOrSchoolAdminRouteGuard)

router.beforeEach((to, from, next) => {
  const routerStore = useRouterStore()
  if (!to.meta.isPremium) {
    routerStore.markLastNonPremiumPage(to)
  }
  next()
})

router.beforeEach(premiumRouteGuard)

router.afterEach((to) => {
  setTitle(to.meta.title)
})

export function setTitle(title: string) {
  document.title = `${title} - Goalbook Toolkit`
}

export default router
